<template>
  <div>
    <div v-if="layout === 'vertical'" class="d-flex">
      <div
        v-show="input && source"
        class="text-center mb-2"
      >
        <RsThumbnail
          :src="source"
          :show-delete-btn="source.endsWith(input)"
          @on-delete="resetFile(), input = ''"
        />
        <span class="text-small">
          {{ $ts('gallery.labels.current') }}
        </span>
      </div>
      <div
        v-show="input && typeof input === 'object'"
        class="text-center mb-2"
      >
        <RsThumbnail
          class="ml-2"
          :src="getFieldUrl"
          show-delete-btn
          @on-delete="handleImageDelete();"
        />
        <span class="text-small">
          {{ $ts('gallery.labels.new') }}
        </span>
      </div>
    </div>
    <div class="d-flex">
      <RsFormFile
        :id="`image-replacer-${id}`"
        ref="file"
        :accept="'image/*'"
        :readonly="disabled"
        :placeholder="'Upload new image'"
        :variant="layout === 'horizontal' ? 'inline-btn' : 'bootstrap'"
        :prompt="prompt"
        @on-upload-triggered="handleUpload($event)"
      />
      <div v-if="layout === 'horizontal' && input" class="d-flex flex-row">
        <div
          v-if="input && source && typeof input === 'string'"
          class="d-flex flex-row"
        >
          <img
            class="horizontal-preview-img ml-2"
            :src="source"
          />
          <div
            class="d-flex flex-row align-items-center ml-2 preview-filename"
          >
            {{ input.split('/').pop()?.split('_').slice(1).join('_') }}
            <RsButton
              variant="delete"
              icon="trash-alt"
              :weight="'far'"
              @click="resetFile(); input = ''"
            />
          </div>
        </div>
        <div
          v-if="input && typeof input === 'object'"
          class="d-flex flex-row"
        >
          <img
            class="horizontal-preview-img ml-2"
            :src="getFieldUrl"
          />
          <div
            v-if="($refs.file as any)?.fileName"
            class="d-flex flex-row align-items-center ml-2 preview-filename"
          >
            {{ ($refs.file as any).fileName }}
            <RsButton
              variant="delete"
              icon="trash-alt"
              :weight="'far'"
              @click="handleImageDelete()"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import RsFormFile from '@/components/_global/RsFormFile.vue';
import { assertRef } from '@/shared/helpers/assert-ref';

export default defineComponent({
  name: 'RsImageReplacer',

  props: {
    id: {
      type: String,
      default: 'replacer',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [String, Object] as PropType<any>,
      default: () => '',
    },
    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so both
     * `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input` consumers
     * work. `undefined` means the parent did not bind v-model (see `boundValue`).
     */
    modelValue: {
      type: [String, Object] as PropType<any>,
      default: undefined,
    },
    initialSrc: {
      type: String,
      default: '',
    },
    /** layout style */
    layout: {
      type: String,
      default: 'vertical',
      validator: (value?: string) => ['vertical', 'horizontal'].indexOf(value ?? 'vertical') !== -1,
    },
    /** RsFormFile Variant (['btn', 'text', 'bootstrap', 'pencil']) */
    variant: {
      type: String,
      default: 'bootstrap',
      validator: (value?: string) => [
        'btn',
        'text',
        'bootstrap',
        'pencil',
        'inline-btn',
      ].indexOf(value ?? 'bootstrap') !== -1,
    },
    /** RsFormFile Prompt */
    prompt: {
      type: String,
      default: '',
    },
  },

  emits: ['input', 'update:modelValue'],

  data() {
    const bound = this.modelValue !== undefined ? this.modelValue : this.value;
    return {
      input: typeof bound === 'string' ? `${bound}` : bound,
      source: this.initialSrc,
    };
  },

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): string | Record<string, any> {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },

    getFieldUrl(): string {
      if (this.input && typeof this.input === 'string') {
        return this.input;
      }

      if (this.input && typeof this.input === 'object' && (this.input as { files: FileList }).files[0]?.size) {
        return URL.createObjectURL(this.input.files[0]);
      }

      return '';
    },
  },

  watch: {
    initialSrc(newVal: string): void {
      // Skip updates mid-upload (input is an object) to prevent re-render loops.
      if (typeof this.input === 'string') {
        this.source = newVal;
      }
    },

    boundValue(): void {
      this.input = typeof this.boundValue === 'string' ? `${this.boundValue}` : this.boundValue;
    },

    input(): void {
      this.$emit('input', this.input);
      this.$emit('update:modelValue', this.input);
    },
  },

  methods: {
    handleImageDelete(): void {
      this.resetFile();
      this.input = `${this.source}`;
    },

    handleUpload(data: { fileDetails: Array<any>, files: FileList }): void {
      this.input = data;
    },

    resetFile(): void {
      assertRef(this.$refs.file, RsFormFile)?.resetFile();
    },
  },
});
</script>
<style lang="scss" scoped>
.horizontal-preview-img {
  height: 2.25rem;
}
.preview-filename {
  color: #707070;
  font-weight: bold;
  font-size: 14px;
}
</style>
